import { Button } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx-republish";
import "./SpreadsheetViewer.scss";

interface SpreadsheetViewerProps {
  fileUrl: string;
  maxRows?: number; // Number of rows to display per page
}

const SpreadsheetViewer: React.FC<SpreadsheetViewerProps> = ({
  fileUrl,
  maxRows = 50,
}) => {
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetsData, setSheetsData] = useState<Record<string, any[][]>>({});
  const [activeSheet, setActiveSheet] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    const fetchAndParse = async () => {
      try {
        setLoading(true);
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const blob = await response.blob();
        const reader = new FileReader();

        reader.onload = (evt) => {
          try {
            const binaryStr = evt.target?.result;
            if (
              typeof binaryStr !== "string" &&
              !(binaryStr instanceof ArrayBuffer)
            ) {
              setError("File could not be read as binary.");
              setLoading(false);
              return;
            }

            // Parse workbook
            const workbook = XLSX.read(binaryStr, {
              type: typeof binaryStr === "string" ? "binary" : "array",
            });

            // Sheet names
            const names = workbook.SheetNames;
            setSheetNames(names);

            // Convert each sheet to 2D array
            const dataObject: Record<string, any[][]> = {};
            names.forEach((name) => {
              const worksheet = workbook.Sheets[name];
              dataObject[name] = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
              });
            });
            setSheetsData(dataObject);

            // Default to first sheet
            if (names.length > 0) {
              setActiveSheet(names[0]);
            }
          } catch (err) {
            console.error(err);
            setError("Error parsing spreadsheet.");
          } finally {
            setLoading(false);
          }
        };

        reader.onerror = () => {
          setError("Error reading file.");
          setLoading(false);
        };
        reader.readAsArrayBuffer(blob);
      } catch (err) {
        console.error(err);
        setError("Error fetching file.");
        setLoading(false);
      }
    };

    fetchAndParse();
  }, [fileUrl]);

  // Reset pagination whenever the active sheet changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSheet]);

  if (loading) {
    return <div>Loading spreadsheet data...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>{error}</div>;
  }

  if (!activeSheet || sheetNames.length === 0) {
    return <div>No sheets found.</div>;
  }

  // Render table content
  const renderTableContent = () => {
    if (!activeSheet || !sheetsData[activeSheet]) {
      return null;
    }

    const data = sheetsData[activeSheet];

    // Check if sheet is empty
    if (
      !data ||
      data.length === 0 ||
      (data.length === 1 && (!data[0] || data[0].length === 0))
    ) {
      return <div className="empty-sheet-message">This sheet is empty</div>;
    }

    const totalRows = data.length;
    const startIndex = (currentPage - 1) * maxRows;
    const endIndex = Math.min(startIndex + maxRows, totalRows);
    const currentData = data.slice(startIndex, endIndex);

    // Calculate column widths based on content
    const columnWidths = data.reduce((widths: number[], row) => {
      row.forEach((cell, index) => {
        const cellContent = String(cell || "");
        widths[index] = Math.max(widths[index] || 0, cellContent.length);
      });
      return widths;
    }, []);

    // Generate unique key for a cell based on its content and position
    const generateCellKey = (
      rowData: any[],
      rowIdx: number,
      cellIdx: number
    ) => {
      const cellValue = String(rowData[cellIdx] || "");
      // Create a stable hash from the cell's content and position
      return `${activeSheet}-${rowIdx}-${cellIdx}-${cellValue}`;
    };

    // Generate unique key for a row based on its content
    const generateRowKey = (rowData: any[], rowIdx: number) => {
      // Create a stable identifier using the first few cells' content
      const rowContent = rowData
        .slice(0, 3)
        .map((cell) => String(cell || ""))
        .join("-");
      return `${activeSheet}-${rowIdx}-${rowContent}`;
    };

    // Render table headers to avoid code duplication
    const renderTableHeaders = () => (
      <tr>
        <th className="row-index-header">#</th>
        {data[0]?.map((header: any, index: number) => (
          <th
            key={generateCellKey(data[0], 0, index)}
            style={{
              minWidth: `${Math.min(Math.max(columnWidths[index] * 8, 100), 300)}px`,
            }}
          >
            {header || `Column ${index + 1}`}
          </th>
        ))}
      </tr>
    );

    // Synchronize scrolling between top and bottom containers
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const containers = document.querySelectorAll(".table-scroll-container");
      containers.forEach((container) => {
        if (container !== target) {
          (container as HTMLDivElement).scrollLeft = target.scrollLeft;
        }
      });
    };

    return (
      <>
        {/* Top scroll bar */}
        <div
          className="table-scroll-container top-scroll"
          onScroll={handleScroll}
        >
          <div style={{ width: "1px", height: "1px", visibility: "hidden" }}>
            <table className="table-preview">
              <thead>{renderTableHeaders()}</thead>
            </table>
          </div>
        </div>

        {/* Main table container with both horizontal and vertical scroll */}
        <div className="table-container">
          <div
            className="table-scroll-container main-scroll"
            onScroll={handleScroll}
          >
            <table className="table-preview">
              <thead>{renderTableHeaders()}</thead>
              <tbody>
                {currentData.map((row: any[], rowIndex: number) => (
                  <tr key={generateRowKey(row, startIndex + rowIndex)}>
                    <td className="row-index">{startIndex + rowIndex + 1}</td>
                    {row.map((cell: any, cellIndex: number) => (
                      <td
                        key={generateCellKey(
                          row,
                          startIndex + rowIndex,
                          cellIndex
                        )}
                        title={String(cell || "")}
                        style={{
                          minWidth: `${Math.min(Math.max(columnWidths[cellIndex] * 8, 100), 300)}px`,
                        }}
                      >
                        {cell || ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="spread-sheet-pagination">
          <Button
            className="pagination-button"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            leftSection={<IconChevronLeft size={16} />}
          >
            Previous
          </Button>
          <span className="page-info">
            Page {currentPage} of {Math.ceil((totalRows - 1) / maxRows)}
          </span>
          <Button
            className="pagination-button"
            onClick={() =>
              setCurrentPage((prev) =>
                Math.min(Math.ceil((totalRows - 1) / maxRows), prev + 1)
              )
            }
            disabled={currentPage >= Math.ceil((totalRows - 1) / maxRows)}
            rightSection={<IconChevronRight size={16} />}
          >
            Next
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="spreadsheet-viewer">
      {/* Sheet Tabs */}
      <div className="sheet-tabs">
        {sheetNames.map((name) => (
          <Button
            key={name}
            onClick={() => setActiveSheet(name)}
            mr={8}
            p={6}
            variant={name === activeSheet ? "filled" : "light"}
            className={`sheet-tab-button ${name === activeSheet ? "active" : ""}`}
          >
            {name}
          </Button>
        ))}
      </div>

      {/* Table Preview */}
      {renderTableContent()}
    </div>
  );
};

export default SpreadsheetViewer;
